import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { QueryClient, useMutation, useQuery } from '@tanstack/react-query'
import { redirect, useNavigate, useParams } from '@tanstack/react-router'
import axios from 'axios'
import { produce } from 'immer'
import { useAtom } from 'jotai'
import { useMemo } from 'react'
import {
	Assistant,
	AssistantConfig,
	AssistantCreate,
	AssistantPromptChangelogEntry,
	Profile,
	ReferenceData,
	ThreadCreate,
	type BotConfig,
	type BotConfigUpdate,
	type Context,
	type DataSet,
	type DataSource,
	type Module,
	type ModuleItem,
	type Project,
	type Thread,
	type ThreadSummery,
} from '~/model'
import { demoThreadAtom, pendingMessageAtom, reshowOnboardingAtom, selectedResourcesAtom } from '~/state'
import { getJwt, isStudent } from '~/utils/utils'
import { useCourseId } from './hooks/useCourseId'

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 2,
			staleTime: 1000 * 5, // 5 seconds
			gcTime: Infinity,
		},
	},
})

export const queryPersister = createAsyncStoragePersister({
	storage: window.localStorage,
})

const apiUrl = localStorage.getItem('apiUrl')
export const axiosAuthInstance = axios.create({
	baseURL: apiUrl != null && apiUrl.length > 0 ? apiUrl : API_BASE_URL,
	headers: {
		Accept: 'application/json',
	},
})

// Intercept all requests and add the auth token
axiosAuthInstance.interceptors.request.use((config) => {
	const token = localStorage.getItem('token')

	if (token == null || token.length === 0) {
		redirect({
			to: '/no-token',
			throw: true,
		})
	}

	const jwt = getJwt()
	if (jwt.payload.exp < Date.now() / 1000) {
		return Promise.reject(new Error('Token expired'))
	}

	config.headers.Authorization = `Bearer ${token}`
	return config
})

export function useApiContext() {
	const courseId = useCourseId()
	return useQuery({
		queryKey: ['context', courseId],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<Context>(`/auth/context`)
			return data
		},
		refetchOnReconnect: 'always',
	})
}

export function useReferenceData() {
	return useQuery({
		queryKey: ['referenceData'],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ referenceData: ReferenceData }>(`/reference-data`)
			return data.referenceData
		},
		staleTime: 1000 * 60 * 60, // 1 hour
	})
}

export function useModules() {
	const courseId = useCourseId()
	return useQuery({
		queryKey: ['course', courseId, 'modules'],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<Module[]>(`/${courseId}/modules`)
			return data
		},
		select: (data) => {
			return data.map((module) => addItemTypeCountToModule(module))
		},
		initialData: [],
		staleTime: 0,
	})
}

export function useIndexModules() {
	const courseId = useCourseId()
	const botConfig = useBotConfig()
	const [selectedResources, setSelectedResources] = useAtom(selectedResourcesAtom)
	const mutation = useMutation({
		mutationFn: async () => {
			const { data } = await axiosAuthInstance.post<Module[]>(`/${courseId}/modules/index?onlyIncludePublishedModules=true&onlyIncludePublishedModuleItems=false`)
			return data
		},
		onSuccess: (data) => {
			queryClient.setQueryData(
				['course', courseId, 'modules'],
				data.map((module) => addItemTypeCountToModule(module)),
			)
			if (botConfig?.dataset_id == null && Object.keys(selectedResources).length == 0) {
				const next: Record<string, boolean> = {}
				data.forEach((module) => {
					module.items.forEach((item) => {
						next[item.id] = true
						item.children?.forEach((child) => {
							next[child.id] = true
						})
					})
				})
				setSelectedResources(next)
			}
		},
	})
	return { indexModules: mutation.mutateAsync, indexModulesIsPending: mutation.isPending }
}

function addItemTypeCountToModule(module: Module) {
	let numberOfChildPages = 0
	let numberOfChildFiles = 0

	function traverseItems(items: ModuleItem[]) {
		for (const item of items) {
			if (item.children) {
				traverseItems(item.children)
			}
			if (item.type === 'page') {
				numberOfChildPages++
			}
			if (item.type === 'file') {
				numberOfChildFiles++
			}
		}
	}

	traverseItems(module.items)
	return { ...module, numberOfChildPages, numberOfChildFiles }
}

export function useBotConfig() {
	const courseId = useCourseId()
	const query = useQuery({
		queryKey: ['tutorbot', courseId, 'config'],
		queryFn: async () => {
			let response = await axiosAuthInstance.get<{ bot_details: BotConfig }>(`/tutorbot/${courseId}/config`)
			// If the bot has not been created yet then create it
			if (response.status === 204) {
				response = await axiosAuthInstance.post<{ bot_details: BotConfig }>(`/tutorbot/${courseId}/config`)
				const previousConfig = queryClient.getQueryData<BotConfig>(['tutorbot', courseId, 'config'])
				if (previousConfig?.locked && !response.data.bot_details.locked) {
					void queryClient.invalidateQueries()
				}
			}
			return response.data.bot_details
		},
	})

	return query.data
}

export function useUpdateBotConfig() {
	const courseId = useCourseId()
	const botConfig = useBotConfig()
	return useMutation({
		onMutate: (botConfigUpdate) => {
			void queryClient.cancelQueries({ queryKey: ['tutorbot', courseId, 'config'] })
			const newBotConfig = botConfigUpdate.locked
				? { ...botConfig, locked: true, is_locked_by_current_user: true, locked_at: new Date().toISOString() }
				: { ...botConfig, locked: false, is_locked_by_current_user: undefined, locked_at: undefined }

			queryClient.setQueryData(['tutorbot', courseId, 'config'], newBotConfig)
		},
		mutationFn: async (botConfigUpdate: BotConfigUpdate) => {
			const { data } = await axiosAuthInstance.patch<{ bot_details: BotConfig }>(`/tutorbot/${courseId}/config`, { botDetails: botConfigUpdate })
			return data.bot_details
		},
		onError: () => {
			void queryClient.invalidateQueries({ queryKey: ['tutorbot', courseId, 'config'] })
		},
	})
}

// DataSet

export function useDataSet() {
	const botConfig = useBotConfig()
	const query = useQuery({
		queryKey: ['dataSet', botConfig?.dataset_id],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ dataset: DataSet }>(`/datasets/${botConfig?.dataset_id ?? ''}`)
			return data.dataset
		},
		enabled: botConfig?.dataset_id != null,
	})
	return { dataSet: query.data }
}

export function useDataSources(includeDependencies = false) {
	const botConfig = useBotConfig()
	const query = useQuery({
		queryKey: ['dataSets', botConfig?.dataset_id, 'dataSources', includeDependencies],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ dataSources: DataSource[] }>(`/datasets/${botConfig?.dataset_id ?? ''}/datasources?${includeDependencies ? 'includeDependencies=true' : ''}`)
			return data.dataSources
		},
		select: (dataSources) => {
			const dataSourcesById: Record<string, DataSource | undefined> = {}
			const readyDataSourcesByIds: Record<string, boolean> = {}
			let hasErrors = false

			dataSources.forEach((dataSource) => {
				const uniqueTag = dataSource.unique_tag ?? dataSource.file_name.split('_')[0]
				dataSourcesById[uniqueTag] = dataSource
				if (dataSource.status === 'READY') {
					readyDataSourcesByIds[uniqueTag] = true
				} else if (dataSource.status === 'ERROR') {
					hasErrors = true
				}
			})
			return { dataSources, dataSourcesById, hasErrors, readyDataSourcesByIds }
		},
		enabled: botConfig?.dataset_id != null,
	})
	return {
		dataSources: query.data?.dataSources,
		dataSourcesById: query.data?.dataSourcesById,
		dataSourcesHasErrors: query.data?.hasErrors,
		readyDataSourcesByIds: query.data?.readyDataSourcesByIds,
	}
}

export function useUpdateDataSet() {
	const courseId = useCourseId()
	return useMutation({
		mutationFn: async (selected: ModuleItem[]) => {
			const { data } = await axiosAuthInstance.put<{ bot_details: BotConfig }>(`/tutorbot/${courseId}/dataset`, { moduleItems: selected })
			return data.bot_details
		},
		onSuccess: (botConfig) => {
			queryClient.setQueryData(['tutorbot', courseId, 'config'], botConfig)
		},
	})
}

// Project

export function useProject() {
	const botConfig = useBotConfig()
	return useQuery({
		queryKey: ['projects', botConfig?.project_id],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ project: Project }>(`/projects/${botConfig?.project_id ?? ''}`)
			return data.project
		},
		enabled: botConfig?.project_id != null,
	})
}

export function useUpdateProject() {
	return useMutation({
		mutationFn: async (project: Project) => {
			const { data } = await axiosAuthInstance.put<{ project: Project }>(`/projects/${project.SK}`, {
				project,
			})
			return data.project
		},
		onSuccess: (data) => {
			void queryClient.invalidateQueries({ queryKey: ['projects', data.SK, 'changelog'] })
		},
	})
}

// Chat completion
export function useChatCompletion() {
	const botConfig = useBotConfig()
	const { appId, templateId } = useParams({ strict: false })
	const assistant = useCurrentAssistant()
	const { data: existingAppThread } = useThread(appId != null)
	const [, setPendingMessage] = useAtom(pendingMessageAtom)
	const [demoThread, setDemoThread] = useAtom(demoThreadAtom)
	return useMutation({
		mutationFn: async (message: string) => {
			if (botConfig?.project_id == null) {
				throw new Error('Project ID is required')
			}
			if (appId == null && templateId == null) {
				throw new Error('App ID or thread ID is required')
			}
			const existingThread = existingAppThread ?? demoThread
			const newThread: ThreadCreate | Thread =
				existingThread == null
					? {
							id: '',
							project: assistant?.isTemplate && assistant.assistantType === 'CHAT' ? 'playground' : botConfig.project_id,
							assistantId: assistant?.isTemplate ? undefined : appId,
							uiMessages: [
								{
									role: 'user',
									content: message,
									llmConfiguration: assistant?.isTemplate ? assistant.llmConfiguration : undefined,
									promptTemplates: assistant?.isTemplate ? { default: { promptAiToKnow: assistant.promptTemplates?.default.promptAiToKnow ?? '' } } : undefined,
								},
							],
							isDemo: assistant?.isTemplate,
							promptTemplateId: Object.keys(assistant?.promptTemplates ?? {}).find((id) => id !== 'default'),
							type: assistant?.isTemplate ? assistant.assistantType : undefined,
						}
					: produce(existingThread, (draft) => {
							draft.uiMessages.push({
								role: 'user',
								content: message,
								promptTemplates: assistant?.isTemplate ? { default: { promptAiToKnow: assistant.promptTemplates?.default.promptAiToKnow ?? '' } } : undefined,
							})
						})
			const { data } = await axiosAuthInstance.post<{ thread: Thread }>(`/projects/${botConfig.project_id}/chat`, {
				thread: newThread,
			})
			setPendingMessage(null)
			return data.thread
		},
		onSuccess: (updatedThread) => {
			if (assistant?.isTemplate) {
				setDemoThread(updatedThread)
				return
			}
			queryClient.setQueryData(['projects', botConfig?.project_id, 'assistants', appId, 'threads', updatedThread.id], updatedThread)
			queryClient.setQueryData(['projects', botConfig?.project_id, 'assistants', appId, 'threads'], (oldData: Thread[] | undefined) => {
				if (oldData == null) return [updatedThread]
				const index = oldData.findIndex((thread) => thread.id === updatedThread.id)
				if (index === -1) return [updatedThread, ...oldData]
				return produce(oldData, (draft) => {
					draft[index] = updatedThread
				})
			})
		},
	})
}

interface TutorbotTopic {
	id: number
	project_id: string
	tutorbot_id: number
	content: string
}

export function useSubjectTopics() {
	const courseId = useCourseId()

	return useQuery({
		queryKey: ['bot', courseId, 'topics'],
		queryFn: async () => {
			if (!courseId) {
				throw new Error('Course ID is required')
			}

			const { data } = await axiosAuthInstance.get<TutorbotTopic[]>(`/tutorbots/${courseId}/topics`)
			return data
		},
		enabled: !!courseId,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchInterval: false,
		staleTime: 1000 * 60 * 60, // 1 hour
	})
}

//  Thread

export function useThreads() {
	const botConfig = useBotConfig()
	const { appId } = useParams({ strict: false })
	return useQuery({
		queryKey: ['projects', botConfig?.project_id, 'assistants', appId, 'threads'],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ threads: ThreadSummery[] }>(`/projects/${botConfig?.project_id}/assistants/${appId}/threads`)
			return data.threads
		},
		enabled: botConfig?.project_id != null && appId != null,
	})
}

export function useThread(disable = false) {
	const botConfig = useBotConfig()
	const { appId, threadId } = useParams({ strict: false })
	return useQuery({
		queryKey: ['projects', botConfig?.project_id, 'assistants', appId, 'threads', threadId],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ thread: Thread }>(`/projects/${botConfig?.project_id}/assistants/${appId}/threads/${threadId}`)
			return data.thread
		},
		enabled: botConfig?.project_id != null && threadId != undefined && appId != null && !disable,
	})
}

export const useDeleteThread = () => {
	const botConfig = useBotConfig()
	const { appId } = useParams({ strict: false })
	return useMutation({
		// Optimistic update
		onMutate: (threadId: string) => {
			queryClient.setQueryData(['projects', botConfig?.project_id, 'assistants', appId, 'threads'], (oldData: Thread[] | undefined) => {
				if (oldData == null) return []
				return produce(oldData, (draft) => {
					const index = draft.findIndex((thread) => thread.id === threadId)
					if (index !== -1) draft.splice(index, 1)
				})
			})
		},
		mutationFn: async (threadId: string) => {
			await axiosAuthInstance.delete(`/projects/${botConfig?.project_id}/assistants/${appId}/threads/${threadId}`)
		},
	})
}

export const useRenameThread = () => {
	return useMutation({
		// Optimistic update
		onMutate: ({ threadId, projectId, assistantId, newName }) => {
			queryClient.setQueryData<Thread[] | undefined>(['projects', projectId, 'assistants', assistantId, 'threads'], (oldData) => {
				if (oldData == null) return []
				return produce(oldData, (draft) => {
					const renamedThread = draft.find((thread) => thread.id === threadId)
					if (renamedThread != null) {
						renamedThread.threadName = newName
					}
				})
			})
			queryClient.setQueryData<Thread | undefined>(['projects', projectId, 'assistants', assistantId, 'threads', threadId], (oldData) => {
				if (oldData == null) return undefined
				return produce(oldData, (draft) => {
					draft.threadName = newName
				})
			})
		},
		mutationFn: async (variables: { projectId: string; assistantId: string; threadId: string; newName: string }) => {
			return await axiosAuthInstance.patch<{ thread: Thread }>(`/projects/${variables.projectId}/assistants/${variables.assistantId}/threads/${variables.threadId}`, {
				thread: {
					threadName: variables.newName,
				},
			})
		},
	})
}

// List of available bots for currently logged in student
export function useBots() {
	const query = useQuery({
		queryKey: ['bots'],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ tutorbots: BotConfig[] }>(`/tutorbots`)
			return data.tutorbots
		},
		staleTime: 1000 * 60 * 60, // 1 hour
	})
	return { bots: query.data, botsIsLoading: query.isLoading }
}

export async function refreshToken() {
	const token = localStorage.getItem('token')
	const jwt = getJwt()

	const expMs = jwt.payload.exp * 1000
	const iatMs = jwt.payload.iat * 1000
	const timeSinceTokenIssued = Date.now() - iatMs
	const timeTillExpiry = expMs - Date.now()

	// If token is older than 1 hour and not expired
	if (timeSinceTokenIssued > 1000 * 60 * 60 && timeTillExpiry > 0) {
		const refreshResponse = await axios.post<{ refresh_token: string }>('/auth/refresh', null, {
			baseURL: API_BASE_URL,
			headers: { Authorization: `Bearer ${token ?? ''}` },
		})

		if (refreshResponse.status === 200) {
			localStorage.setItem('token', refreshResponse.data.refresh_token)
		}
	}
}

// Profile

function useProfile() {
	return useQuery({
		queryKey: ['profile'],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ user: Profile }>('/user/me')
			return data.user
		},
	})
}

function useUpdateProfile() {
	const { data: context } = useApiContext()
	return useMutation({
		mutationFn: async (profile: Profile) => {
			const updatedProfile = produce(profile, (draft) => {
				draft.firstName = context?.context.given_name ?? ''
				draft.lastName = context?.context.family_name ?? ''
				draft.email = context?.context.canvas_user_email_id ?? ''
			})
			const { data } = await axiosAuthInstance.put<{ user: Profile }>('/user/me', { user: updatedProfile })
			return data.user
		},
	})
}

export function useHasOnboarded() {
	return true
	// const { data: profile } = useProfile()
	// const [reshowOnboarding] = useAtom(reshowOnboardingAtom)

	// if (reshowOnboarding) {
	// 	return false // Force show onboarding if reshowOnboarding is true
	// }

	// // Otherwise check if they've completed onboarding before
	// return profile == null || profile.appData.ailearningassistant.onboarded
}

export function useSetHasOnboarded() {
	const { data: profile } = useProfile()
	const { mutate: updateProfile } = useUpdateProfile()
	const [, setReshowOnboarding] = useAtom(reshowOnboardingAtom)

	return () => {
		if (profile == null) return
		const updatedProfile = produce(profile, (draft) => {
			draft.appData.ailearningassistant.onboarded = true
		})
		queryClient.setQueryData(['profile'], updatedProfile)
		updateProfile(updatedProfile)
		setReshowOnboarding(false)
	}
}

// Assistant

const revisionOpeningMessage =
	'Revision coach allows the AI to act as a tutor and ask you questions to better understand your knowledge. It will then guide you through tasks and recommend appropriate resources to study.'

export function useAssistants() {
	const botConfig = useBotConfig()
	const { data: assistantConfigs } = useAssistantConfigs()
	const { data: assistants } = useQuery({
		queryKey: ['projects', botConfig?.project_id, 'assistants'],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ assistants: Assistant[] }>(`/projects/${botConfig?.project_id}/assistants`)
			return data.assistants
		},
		enabled: botConfig?.project_id != null,
	})
	return useMemo(
		() =>
			assistants
				?.map((assistant): Assistant => {
					const isEnabled = assistantConfigs?.find((config) => config.assistant_id === assistant.id)?.enabled
					const isEnabledForRole = !isStudent() || isEnabled
					const isAvailable = assistant.assistantType === 'CHAT' || botConfig?.status === 'Ready'
					const isEnabledAndAvailable = isAvailable && isEnabled
					const isEnabledAndAvailableForRole = isAvailable && isEnabledForRole

					return {
						...assistant,
						isEnabled,
						isEnabledForRole,
						isAvailable,
						isAvailableAndEnabled: isEnabledAndAvailable,
						isAvailableAndEnabledForRole: isEnabledAndAvailableForRole,
						openingMessage: assistant.promptTemplates?.SOCRATIC != null ? revisionOpeningMessage : assistant.openingMessage,
					}
				})
				.toSorted((a, b) => b.name.localeCompare(a.name))
				.toSorted((a, b) => (a.isGlobal ? -1 : b.isGlobal ? 1 : 0)) ?? [],
		[assistants, assistantConfigs, botConfig?.status],
	)
}

export function useCurrentAssistant() {
	const { appId, templateId } = useParams({ strict: false })
	return useAssistant(appId ?? templateId ?? '')
}

export function useAssistant(appId: string) {
	const assistants = useAssistants()
	const { data: templates } = useAssistantTemplates()
	return useMemo(() => {
		const app = assistants.find((assistant) => assistant.id === appId)
		if (app != null) return app
		return templates?.find((template) => template.id === appId)
	}, [assistants, templates, appId])
}

export function useCreateAssistant() {
	const botConfig = useBotConfig()
	const navigate = useNavigate()
	return useMutation({
		mutationFn: async (assistant: AssistantCreate) => {
			const { data } = await axiosAuthInstance.post<{ assistant: Assistant }>(`/projects/${botConfig?.project_id ?? ''}/assistants`, { assistant })
			return data.assistant
		},
		onSuccess: (assistant) => {
			queryClient.setQueryData(['projects', botConfig?.project_id, 'assistants'], (oldData: Assistant[] | undefined) => {
				if (oldData == null) return [assistant]
				return [assistant, ...oldData]
			})
			void navigate({ from: '/educator/courses/$courseId/apps', to: './$appId', params: { appId: assistant.id } })
		},
	})
}

export function useUpdateAssistant() {
	const botConfig = useBotConfig()
	return useMutation({
		mutationFn: async (assistant: Assistant) => {
			const { data } = await axiosAuthInstance.put<{ assistant: Assistant }>(`/projects/${botConfig?.project_id ?? ''}/assistants/${assistant.id}`, {
				assistant: { ...assistant, isGlobal: undefined, enabled: undefined, available: undefined },
			})
			return data.assistant
		},
		onSuccess: (assistant) => {
			queryClient.setQueryData(['projects', botConfig?.project_id, 'assistants'], (oldData: Assistant[] | undefined) => {
				if (oldData == null) return [assistant]
				return oldData.map((a) => (a.id === assistant.id ? assistant : a))
			})
			void queryClient.invalidateQueries({ queryKey: ['projects', botConfig?.project_id, 'assistants'] })
		},
	})
}

export function useDeleteAssistant() {
	const botConfig = useBotConfig()
	return useMutation({
		onMutate: (assistantId) => {
			queryClient.setQueryData(['projects', botConfig?.project_id, 'assistants'], (oldData: Assistant[] | undefined) => {
				if (oldData == null) return []
				return oldData.filter((assistant) => assistant.id !== assistantId)
			})
		},
		mutationFn: async (assistantId: string) => {
			await axiosAuthInstance.delete(`/projects/${botConfig?.project_id ?? ''}/assistants/${assistantId}`)
		},
		onError: () => {
			void queryClient.invalidateQueries({ queryKey: ['projects', botConfig?.project_id, 'assistants'] })
		},
	})
}

// Assistant prompt changelog

export function useAssistantPromptChangelog() {
	const botConfig = useBotConfig()
	const { appId } = useParams({ strict: false })
	const assistant = useCurrentAssistant()
	return useQuery({
		queryKey: ['projects', botConfig?.project_id, 'assistants', appId, 'changelog'],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ changeLogs: AssistantPromptChangelogEntry[] }>(`/projects/${botConfig?.project_id}/assistants/${appId}/changelog?count=20`)
			return data.changeLogs.filter((log) => log.changes.promptTemplates?.default.change.previousPrompt != null).reverse()
		},
		enabled: botConfig?.project_id != null && appId?.length !== 0 && assistant != null && !assistant.isTemplate,
	})
}

export function useChangelogUserFullNames(changelog?: AssistantPromptChangelogEntry[]) {
	const courseId = useCourseId()
	const usernames = Array.from(new Set(changelog?.map((entry) => entry.user))).join(',')
	return useQuery({
		queryKey: ['course', courseId, 'usernames', usernames],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<Record<string, string>[]>(`/user-full-names-by-course/${courseId}?usernames=${usernames}`)
			return data.reduce<Record<string, string>>((acc, current) => {
				const [key, value] = Object.entries(current)[0]
				acc[key] = value
				return acc
			}, {})
		},
		enabled: usernames.length > 0,
	})
}

// Assistant Configs

function useAssistantConfigs() {
	const courseId = useCourseId()
	return useQuery({
		queryKey: ['tutorbot', courseId, 'assistant-configs'],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ tutor_bot_assistants: AssistantConfig[] }>(`/tutorbot/${courseId}/assistants?configs`)
			return data.tutor_bot_assistants
		},
		staleTime: 0,
	})
}

export function useAssistantConfig(assistantId?: string) {
	const { data: assistantConfigs } = useAssistantConfigs()
	return useMemo(() => {
		return assistantConfigs?.find((config) => config.assistant_id === assistantId)
	}, [assistantConfigs, assistantId])
}

export function useEnabledAssistants() {
	const assistants = useAssistants()
	return useMemo(() => {
		return assistants.filter((assistant) => assistant.isEnabled)
	}, [assistants])
}

export function useUpdateAssistantConfig() {
	const courseId = useCourseId()
	const { data: assistantConfigs } = useAssistantConfigs()
	return useMutation({
		onMutate: ({ assistantId, enabled }) => {
			queryClient.setQueryData(['tutorbot', courseId, 'assistant-configs'], (oldData: AssistantConfig[] | undefined) => {
				if (oldData == null) return []
				return oldData.map((config) => (config.assistant_id === assistantId ? { ...config, enabled } : config))
			})
		},
		mutationFn: async ({ assistantId, enabled }: { assistantId: string; enabled: boolean }) => {
			const assistantConfig = assistantConfigs?.find((config) => config.assistant_id === assistantId)
			const { data } = await axiosAuthInstance.patch<{ assistantDetails: AssistantConfig }>(`/tutorbot/${courseId}/assistants/${assistantId}/config`, {
				assistantDetails: {
					...assistantConfig,
					enabled,
				},
			})
			return data.assistantDetails
		},
		onSuccess: () => {
			setTimeout(() => {
				void queryClient.invalidateQueries({ queryKey: ['tutorbot', courseId, 'assistant-configs'] })
			}, 1000)
		},
	})
}

// Assistant Templates

export function useAssistantTemplates() {
	const botConfig = useBotConfig()
	const userId = getJwt().payload.sub
	return useQuery({
		queryKey: ['assistantTemplates'],
		queryFn: async () => {
			const { data } = await axiosAuthInstance.get<{ templates: Assistant[] }>('/assistant-templates')

			return data.templates
				.filter((template) => !template.isGlobalDefault)
				.map((template): Assistant => {
					const isEnabled = false
					const isEnabledForRole = true
					const isAvailable = template.assistantType === 'CHAT' || botConfig?.status === 'Ready'
					const isAvailableAndEnabled = isAvailable && isEnabled
					const isAvailableAndEnabledForRole = isAvailable && isEnabledForRole
					const currentUserIsOwner = template.ownerId === userId

					return {
						...template,
						isTemplate: true,
						iconTheme: '#f1f1f1',
						isEnabled,
						isEnabledForRole,
						isAvailable,
						isAvailableAndEnabled,
						isAvailableAndEnabledForRole,
						currentUserIsOwner,
					}
				})
		},
		placeholderData: [],
		enabled: !isStudent(),
	})
}

export function useCreateAssistantTemplate() {
	return useMutation({
		mutationFn: async ({ assistant, tags }: { assistant: Assistant; tags: string[] }) => {
			const { data } = await axiosAuthInstance.post<{ template: Assistant }>('/assistant-templates', {
				projectId: assistant.projectId,
				assistantId: assistant.id,
				tags,
			})
			return data.template
		},
		onSuccess: (template) => {
			queryClient.setQueryData(['assistantTemplates'], (oldData: Assistant[] | undefined) => {
				if (oldData == null) return [{ ...template, isTemplate: true }]
				return [...oldData, { ...template, isTemplate: true }]
			})
			void queryClient.invalidateQueries({ queryKey: ['assistantTemplates'] })
		},
	})
}

export function useCreateAssistantFromTemplate() {
	const createAssistant = useCreateAssistant()

	function createAssistantFromTemplate(template: Assistant) {
		createAssistant.mutate({
			name: template.name,
			originAssistantTemplateId: template.id,
		})
	}

	return { createAssistantFromTemplate, ...createAssistant }
}

export function useDeleteAssistantTemplate() {
	return useMutation({
		onMutate: (assistantId) => {
			void queryClient.cancelQueries({ queryKey: ['assistantTemplates'] })
			queryClient.setQueryData(['assistantTemplates'], (oldData: Assistant[] | undefined) => {
				if (oldData == null) return []
				return oldData.filter((assistant) => assistant.id !== assistantId)
			})
		},
		mutationFn: async (assistantId: string) => {
			await axiosAuthInstance.delete(`/assistant-templates/${assistantId}`)
		},
		onError: () => {
			void queryClient.invalidateQueries({ queryKey: ['assistantTemplates'] })
		},
	})
}
